import { useState, useEffect, useRef } from "react";
import "./CheckoutScreen.css";
import { url, API_KEY } from "../../Constant/TextConstant.js";

// AddressDialog component for adding/editing addresses with location autocomplete
const AddressDialog = ({
  isOpen,
  onClose,
  onSave,
  initialData = {},
  isEditing,
}) => {
  const [formData, setFormData] = useState({
    name: initialData.name || "",
    mobile: initialData.mobile || "",
    locality: initialData.locality || "",
    city: initialData.city || "",
    state: initialData.state || "",
    pinCode: initialData.pinCode || "",
    fullAddress: initialData.fullAddress || "",
    landmark: initialData.landmark || "",
    latitude: initialData.latitude || 0,
    longitude: initialData.longitude || 0,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [isFormValid, setIsFormValid] = useState(false);

  const localityRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Fetch suggestions when locality changes
    if (name === "locality" && value.length > 2) {
      fetchLocationSuggestions(value);
    } else if (name === "locality" && value.length <= 2) {
      setShowSuggestions(false);
      setLocationSuggestions([]);
    }
  };

  // Validate form whenever formData changes
  useEffect(() => {
    const isValid =
      formData.name.trim() &&
      formData.mobile.trim().length === 10 &&
      /^\d+$/.test(formData.mobile) &&
      formData.locality.trim() &&
      formData.city.trim() &&
      formData.state.trim() &&
      formData.pinCode.trim().length === 6 &&
      /^\d+$/.test(formData.pinCode) &&
      formData.fullAddress.trim();

    setIsFormValid(isValid);
  }, [formData]);

  useEffect(() => {
    setFormData({
      name: initialData.name || "",
      mobile: initialData.mobile || "",
      locality: initialData.locality || "",
      city: initialData.city || "",
      state: initialData.state || "",
      pinCode: initialData.pinCode || "",
      fullAddress: initialData.fullAddress || "",
      landmark: initialData.landmark || "",
      latitude: initialData.latitude || 0,
      longitude: initialData.longitude || 0,
    });
  }, [initialData]);

  useEffect(() => {
    // Handle clicks outside the suggestions box
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        localityRef.current &&
        !localityRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchLocationSuggestions = async (query) => {
    if (query.length < 3) {
      setShowSuggestions(false);
      setLocationSuggestions([]);
      return;
    }

    setIsFetchingSuggestions(true);
    setShowSuggestions(true);

    try {
      const response = await fetch(
        `https://api.olamaps.io/places/v1/autocomplete?input=${encodeURIComponent(
          query
        )}&language=English&api_key=${API_KEY.OLA_API_KEY}`,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Origin: "https://nearmeind.com",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.status?.toString().toLowerCase() === "ok") {
          setLocationSuggestions(data.predictions || []);
        }
      } else {
      }
    } catch (error) {
    } finally {
      setIsFetchingSuggestions(false);
    }
  };

  const fetchCoordinates = async (locationName) => {
    try {
      const response = await fetch(
        `https://api.olamaps.io/places/v1/geocode?address=${encodeURIComponent(
          locationName
        )}&language=English&api_key=${API_KEY.OLA_API_KEY}`,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Origin: "https://nearmeind.com",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (
          data.status?.toString().toLowerCase() === "ok" &&
          data.geocodingResults &&
          data.geocodingResults.length > 0
        ) {
          const lat = data.geocodingResults[0].geometry.location.lat;
          const lng = data.geocodingResults[0].geometry.location.lng;
          return { lat, lng };
        }
      }
      throw new Error("Failed to fetch coordinates");
    } catch (error) {
      throw error;
    }
  };

  const onSuggestionSelected = async (suggestion) => {
    const description = suggestion.description || "";
    const terms = suggestion.terms || [];

    // Extract address components from the suggestion
    let city = "";
    let state = "";
    let pinCode = "";

    if (terms.length >= 4) {
      city = terms[terms.length - 4].value || "";
    }
    if (terms.length >= 3) {
      state = terms[terms.length - 3].value || "";
    }
    if (terms.length >= 2) {
      pinCode = terms[terms.length - 2].value || "";
    }

    setFormData((prev) => ({
      ...prev,
      locality: description,
      city: city,
      state: state,
      pinCode: pinCode,
      fullAddress: description,
    }));

    setShowSuggestions(false);

    try {
      const coordinates = await fetchCoordinates(description);
      if (coordinates) {
        setFormData((prev) => ({
          ...prev,
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        }));
      }
    } catch (error) {
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      await onSave(formData);
    } catch (error) {
      alert("Error saving address. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      alert("Please enter name");
      return false;
    }
    if (
      !formData.mobile.trim() ||
      formData.mobile.length !== 10 ||
      !/^\d+$/.test(formData.mobile)
    ) {
      alert("Please enter valid 10-digit mobile number");
      return false;
    }
    if (!formData.locality.trim()) {
      alert("Please enter locality");
      return false;
    }
    if (!formData.city.trim()) {
      alert("Please enter city");
      return false;
    }
    if (!formData.state.trim()) {
      alert("Please enter state");
      return false;
    }
    if (
      !formData.pinCode.trim() ||
      formData.pinCode.length !== 6 ||
      !/^\d+$/.test(formData.pinCode)
    ) {
      alert("Please enter valid 6-digit pincode");
      return false;
    }
    if (!formData.fullAddress.trim()) {
      alert("Please enter full address");
      return false;
    }
    return true;
  };

  if (!isOpen) return null;

  return (
    <div className="checkout-dialog-overlay">
      <div className="checkout-address-dialog-enhanced">
        <div className="checkout-dialog-header-enhanced">
          <h3>{isEditing ? "Edit Address" : "Add New Address"}</h3>
          <button onClick={onClose} className="checkout-close-btn-enhanced">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="checkout-address-form">
          <div className="checkout-form-group-enhanced">
            <div className="checkout-input-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
                required
                className="checkout-input-field"
              />
            </div>

            <div className="checkout-input-group">
              <label htmlFor="mobile">Mobile Number *</label>
              <input
                type="text"
                id="mobile"
                name="mobile"
                placeholder="10-digit mobile number"
                value={formData.mobile}
                onChange={handleChange}
                required
                pattern="[0-9]{10}"
                title="Please enter a valid 10-digit mobile number"
                className="checkout-input-field"
                maxLength="10"
              />
            </div>
          </div>

          <div className="checkout-input-group checkout-locality-group-enhanced">
            <label htmlFor="locality">Locality *</label>
            <div
              ref={localityRef}
              className="checkout-locality-wrapper-enhanced"
            >
              <input
                type="text"
                id="locality"
                name="locality"
                placeholder="Start typing your locality"
                value={formData.locality}
                onChange={handleChange}
                onFocus={() => {
                  if (formData.locality.length > 2) setShowSuggestions(true);
                }}
                required
                className="checkout-input-field"
              />
              {isFetchingSuggestions && (
                <div className="checkout-suggestion-loader">
                  <div className="checkout-spinner"></div>
                </div>
              )}
            </div>

            {showSuggestions && (
              <div
                ref={suggestionsRef}
                className="checkout-suggestions-container-enhanced"
              >
                {isFetchingSuggestions ? (
                  <div className="checkout-suggestions-loading">
                    <div className="checkout-mini-spinner"></div>
                    Loading suggestions...
                  </div>
                ) : locationSuggestions.length === 0 ? (
                  <div className="checkout-no-suggestions">
                    No suggestions found. Please try a different search.
                  </div>
                ) : (
                  <ul className="checkout-suggestions-list-enhanced">
                    {locationSuggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        className="checkout-suggestion-item-enhanced"
                        onClick={() => onSuggestionSelected(suggestion)}
                      >
                        <span className="checkout-suggestion-icon">📍</span>
                        <div className="checkout-suggestion-text">
                          <div className="checkout-suggestion-main">
                            {suggestion.description}
                          </div>
                          {suggestion.terms && (
                            <div className="checkout-suggestion-details">
                              {suggestion.terms.slice(0, 3).map((term, i) => (
                                <span
                                  key={i}
                                  className="checkout-suggestion-term"
                                >
                                  {term.value}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="checkout-form-group-enhanced">
            <div className="checkout-input-group">
              <label htmlFor="city">City *</label>
              <input
                type="text"
                id="city"
                name="city"
                placeholder="City"
                value={formData.city}
                onChange={handleChange}
                required
                className="checkout-input-field checkout-readonly-field"
                readOnly
              />
            </div>

            <div className="checkout-input-group">
              <label htmlFor="state">State *</label>
              <input
                type="text"
                id="state"
                name="state"
                placeholder="State"
                value={formData.state}
                onChange={handleChange}
                required
                className="checkout-input-field checkout-readonly-field"
                readOnly
              />
            </div>
          </div>

          <div className="checkout-form-group-enhanced">
            <div className="checkout-input-group">
              <label htmlFor="pinCode">PIN Code *</label>
              <input
                type="text"
                id="pinCode"
                name="pinCode"
                placeholder="6-digit PIN code"
                value={formData.pinCode}
                onChange={handleChange}
                required
                pattern="[0-9]{6}"
                title="Please enter a valid 6-digit PIN code"
                className="checkout-input-field checkout-readonly-field"
                readOnly
                maxLength="6"
              />
            </div>

            <div className="checkout-input-group">
              <label htmlFor="landmark">Landmark (Optional)</label>
              <input
                type="text"
                id="landmark"
                name="landmark"
                placeholder="Nearby landmark for easy delivery"
                value={formData.landmark}
                onChange={handleChange}
                className="checkout-input-field"
              />
            </div>
          </div>

          <div className="checkout-input-group">
            <label htmlFor="fullAddress">Full Address *</label>
            <textarea
              id="fullAddress"
              name="fullAddress"
              placeholder="Complete delivery address including house/building details"
              value={formData.fullAddress}
              onChange={handleChange}
              required
              className="checkout-address-textarea-enhanced"
              rows={4}
            />
          </div>

          <div className="checkout-dialog-actions-enhanced">
            <button
              type="button"
              onClick={onClose}
              className="checkout-cancel-btn-enhanced"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="checkout-save-btn-enhanced"
              disabled={isLoading || !isFormValid}
            >
              {isLoading ? (
                <span className="checkout-button-spinner"></span>
              ) : isEditing ? (
                "Update Address"
              ) : (
                "Save Address"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddressDialog;
